import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
  Grid,
  Link,
  Divider
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container maxWidth={false}>
      <Box sx={{ py: 0 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Contacto
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            ¿Necesitas ayuda personalizada? Estamos aquí para asistirte. Nuestro equipo de soporte está disponible para resolver tus dudas.
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Columna izquierda - Texto de introducción */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 2,
                p: 3,
                height: '100%'
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.text.primary,
                  mb: 2,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                }}
              >
                Contacta con nosotros
              </Typography>

              <Divider sx={{ mb: 3, backgroundColor: theme.palette.divider }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HelpOutlineIcon sx={{ color: theme.palette.primary.main, mr: 2 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'medium', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    ¿Necesitas ayuda personalizada?
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    Estamos aquí para asistirte. Nuestro equipo de soporte está disponible para resolver tus dudas y ayudarte a optimizar tu experiencia con AI4Assistant.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <SupportAgentIcon sx={{ color: theme.palette.primary.main, mr: 2 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'medium', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    Asistencia técnica
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    No dudes en contactarnos para cualquier consulta sobre nuestros servicios, asistencia técnica o información adicional. Nos esforzamos por proporcionar respuestas rápidas y soluciones efectivas a todas tus necesidades.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Columna derecha - Información de contacto */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 2,
                p: 3,
                height: '100%'
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.text.primary,
                  mb: 2,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                }}
              >
                Información de contacto
              </Typography>

              <Divider sx={{ mb: 3, backgroundColor: theme.palette.divider }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ color: theme.palette.primary.main, mr: 2 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'medium', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    Email de soporte:
                  </Typography>
                  <Link
                    href="mailto:soporte@ai4logic.com"
                    sx={{
                      color: theme.palette.text.secondary,
                      textDecoration: 'none',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',

                      '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' }
                    }}
                  >
                    soporte@ai4logic.com
                  </Link>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ color: theme.palette.primary.main, mr: 2 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'medium', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    Email general:
                  </Typography>
                  <Link
                    href="mailto:info@ai4logic.com"
                    sx={{
                      color: theme.palette.text.secondary,
                      textDecoration: 'none',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' }
                    }}
                  >
                    info@ai4logic.com
                  </Link>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LanguageIcon sx={{ color: theme.palette.primary.main, mr: 2 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'medium', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    Sitio web:
                  </Typography>
                  <Link
                    href="https://ai4logic.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: theme.palette.text.secondary,
                      textDecoration: 'none',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' }
                    }}
                  >
                    https://ai4logic.com/
                  </Link>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Contact;
